import React, { type JSX } from "react";

import ChatBody from "./components/chatbot/ChatBody";
import {
  DropTargetOverlay,
  DropErrorToast,
  HistoryLoadingOverlay,
} from "./components/chatbot/ChatOverlays";
import { useChatAnimationStyles, useChatData, useHistoryOverlay } from "./components/chatbot/useChatData";
import { useUseCaseNotice } from "./components/chatbot/useChatActions";
import { useDropUpload } from "./hooks/useDropUpload";
import { useChatScroll } from "./hooks/useChatScroll";
import { useChatFeedback } from "./hooks/useChatFeedback";

interface ChatbotLayoutProps {
  chatInputAriaLabel?: string;
  inWorkspaceMode?: boolean;
}

/**
 * The chat surface: transcript, activity indicators, drag-and-drop uploads and
 * the message composer.
 *
 * Phase 7: streaming animation is handled by RTK Listener middleware; the new
 * system reads displayedText per-message using useStreamingMessage(messageId).
 */
export default function ChatbotLayout_Component({
  chatInputAriaLabel = "Message input field",
  inWorkspaceMode = false,
}: ChatbotLayoutProps): JSX.Element {
  useChatAnimationStyles();

  const useCase = useUseCaseNotice();
  const drop = useDropUpload(useCase.isDropEnabled);
  const chat = useChatData();
  const overlay = useHistoryOverlay(chat.chatHistoryLoading);
  const scroll = useChatScroll(
    chat.chatMessages,
    chat.currentTurnTodoPlan,
    chat.streamingMessages
  );
  const feedback = useChatFeedback(chat.chatMessages, scroll.scrollToBottom);

  return (
    <div
      className={`w-full h-full flex flex-col font-cvcBody relative ${
        overlay.showLoadingOverlay ? "bg-transparent" : "bg-white"
      }`}
      onDragOver={drop.handleDragOver}
      onDragEnter={drop.handleDragOver}
      onDragLeave={drop.handleDragLeave}
      onDrop={(e) => void drop.handleDrop(e)}
    >
      {drop.isDraggingOver && <DropTargetOverlay />}

      {drop.dropError && (
        <DropErrorToast dropError={drop.dropError} onDismiss={() => drop.setDropError("")} />
      )}

      {overlay.showLoadingOverlay ? (
        <HistoryLoadingOverlay visible={overlay.loadingOverlayVisible} />
      ) : (
        <ChatBody
          chat={chat}
          useCase={useCase}
          feedback={feedback}
          scroll={scroll}
          inWorkspaceMode={inWorkspaceMode}
          chatInputAriaLabel={chatInputAriaLabel}
        />
      )}
    </div>
  );
}
